/**
 * Counting what happens in the builder, through the vendor's analytics service.
 *
 * ⚠️ CONSUMED, NEVER VENDORED (CLAUDE.md §4). The beacon is `a.js` from
 * `analytics.ali-web-services.com`, loaded by `index.html`; this file is only the typed way to call
 * the one function it exposes. Nothing here stores, batches or retries — the service's entire claim
 * is that no record of an individual exists at any point, and a client-side queue would be the first
 * place one could.
 *
 * ⚠️ THE VENDOR'S API TAKES A NAME AND NOTHING ELSE, AND THAT IS DELIBERATE ON ITS SIDE. Its own
 * documentation is explicit: an events API that accepts a properties object is the one that ends up
 * carrying a user id, an email or a cart's contents. So there is no `track(name, props)` to write
 * here even if somebody wanted one — the signature cannot express it.
 *
 * ⚠️ AND AN UNDECLARED NAME IS DROPPED SERVER-SIDE, SILENTLY, WHICH IS WHY THE NAMES ARE A UNION.
 * The service counts events exactly rather than through a sketch — that is what makes a conversion a
 * number you can act on rather than a row in `(other)` — and counting an unbounded key space exactly
 * is a denial of service, so it only counts names the account registered in the console. The failure
 * mode for a typo is therefore NOTHING AT ALL: no error, no console warning, no row. A string literal
 * would make `generate_faild` a silent no-op discovered weeks later by somebody wondering why the
 * funnel has a hole. The union makes it a build failure.
 *
 * **The names below must exist in the analytics console or they are counted nowhere.**
 */

/**
 * ⚠️ THE THREE ARE A FUNNEL, NOT THREE FACTS. `done` alone answers "how many sites were generated",
 * which is the number somebody asks for first and the least useful of the three: a generation that
 * starts and never finishes is the failure worth knowing about, and it is invisible unless the start
 * is counted too. `failed` separates "the model refused / the key is wrong" from "they changed their
 * mind", which are opposite problems with opposite fixes.
 */
export const EVENTS = ['generate_start', 'generate_done', 'generate_failed'] as const

export type EventName = (typeof EVENTS)[number]

declare global {
  interface Window {
    /** Installed by `a.js`. Absent until it loads, and absent for ever if it is blocked. */
    aliweb?: (name: string) => void
  }
}

/**
 * Count one event.
 *
 * ⚠️ IT IS SAFE BEFORE THE SCRIPT LOADS AND SAFE IF IT NEVER DOES. The tag is `defer`, so a customer
 * who presses the button on a slow connection can beat it; and an ad blocker removes it outright.
 * Analytics may never be the reason a customer cannot build a website, so this does nothing rather
 * than throwing into the middle of the generate flow.
 */
export function track(name: EventName): void {
  try {
    window.aliweb?.(name)
  } catch {
    /* the beacon is not a dependency of anything working */
  }
}
